import React from 'react';
import { Images } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

// Speed in seconds per photo — scales with gallery size
const SPEED_PER_PHOTO = { slow: 8, normal: 5, fast: 3 };
const MIN_DURATION = 20;

// Height classes for images
const HEIGHT_CLASSES = {
  small: 'h-48 sm:h-56',
  medium: 'h-56 sm:h-72',
  large: 'h-72 sm:h-96',
};

// Gap classes between images
const GAP_CLASSES = { compact: 'gap-2', normal: 'gap-4', spacious: 'gap-6' };

/**
 * MediaGalleryRenderer - Auto-scrolling horizontal photo gallery
 * Uses CSS @keyframes translateX(-50%) with duplicated content for seamless loop.
 * Same proven pattern as SlidingTextRenderer.
 */
const MediaGalleryRenderer = ({ block, photos = [], theme = {} }) => {
  const { content } = block;
  const {
    heading,
    maxPhotos = 0,
    autoScrollSpeed = 'normal',
    imageHeight = 'medium',
    gap = 'normal',
    pauseOnHover = true,
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  // Filter and limit photos
  let displayPhotos = photos.length > 0 ? photos : (content.photos || []);
  if (maxPhotos > 0) {
    displayPhotos = displayPhotos.slice(0, maxPhotos);
  }

  const perPhoto = SPEED_PER_PHOTO[autoScrollSpeed] || SPEED_PER_PHOTO.normal;
  const duration = Math.max(MIN_DURATION, displayPhotos.length * perPhoto);
  const heightClass = HEIGHT_CLASSES[imageHeight] || HEIGHT_CLASSES.medium;
  const gapClass = GAP_CLASSES[gap] || GAP_CLASSES.normal;

  // Unique animation name per block instance to avoid conflicts
  const animName = `media-gallery-scroll-${block.id}`;

  // No photos state
  if (displayPhotos.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <Images size={48} className="mx-auto mb-4" style={{ color: styles.mutedColor }} />
          <p style={{ color: styles.mutedColor }}>No photos to display</p>
        </div>
      </div>
    );
  }

  // Build image strip (rendered twice for seamless loop)
  const ImageStrip = () => (
    <>
      {displayPhotos.map((photo, i) => (
        <div key={`${photo.id}-${i}`} className="flex-shrink-0 group">
          <img
            src={photo.url}
            alt={photo.title || `Photo ${i + 1}`}
            className={`${heightClass} w-auto object-cover rounded-lg`}
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {photo.title && (
            <p
              className="text-sm font-bold text-center mt-2 px-1 truncate opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              style={{ color: styles.bodyColor }}
            >
              {photo.title}
            </p>
          )}
        </div>
      ))}
    </>
  );

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      {/* Section Heading (constrained width) */}
      {heading && (
        <div className="max-w-6xl mx-auto">
          <h3
            className={`${styles.headingSizeClass} font-bold ${styles.headingAlignmentClass} mb-6 sm:mb-8 md:mb-10`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        </div>
      )}

      {/* Full-width scrolling container */}
      <style>{`
        @keyframes ${animName} {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .${animName} {
          animation: ${animName} ${duration}s linear infinite;
        }
        ${pauseOnHover ? `.${animName}:hover { animation-play-state: paused; }` : ''}
      `}</style>

      <div className="w-full overflow-hidden">
        <div className={`${animName} flex ${gapClass} items-start w-max`}>
          {/* First copy */}
          <div className={`flex ${gapClass} items-start shrink-0`}>
            <ImageStrip />
          </div>
          {/* Duplicate for seamless loop */}
          <div className={`flex ${gapClass} items-start shrink-0`}>
            <ImageStrip />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaGalleryRenderer;
